import { useContext } from "react";
import {
    signUp, confirmSignUp, autoSignIn, fetchAuthSession,
    signOut, signIn, confirmSignIn, resendSignUpCode
} from "aws-amplify/auth";
import useRole from "./useRole";
import useApi from "./useApi";
import useEnv from "./useEnv";
import useI18n from "./useI18n";
import usePassword from "./usePassword";
import isEmpty from "lodash.isempty";
import { AuthContext, CUSTOMERTYPE } from "../contexts/AuthContext";
import { UserAccountContext } from "../contexts/UserAccountContext";

const AUTH_STATUS_TUPAS_NEEDED = 'tupasNeeded';

const useUserAccount = () => {
    const {
        authInitialize, authStatus, authRoles, authCompany,
        authCustomerType, authName, authLoggedIn, authSignOut,
        authChosenCustomerType, authSetChosenCustomerType, authUserHasAccount
    } = useContext(AuthContext);
    const {
        clearAccountData,
        updateAccounts,
        accounts
    } = useContext(UserAccountContext);
    const { role } = useRole();
    const { postJson, getJson, deleteJson } = useApi();
    const { getLocaleBCP47 } = useI18n();
    const { getEnv, isEnvTruthy, isDev } = useEnv();
    const { generateRandomPassword } = usePassword();

    /**
     * User sign up
     * @param {object} attributes
     * @returns {Promise<void>}
     */
    const userSignUp = async (attributes) => {
        const password = generateRandomPassword(16, true, true, true, true);
        await signUp({
                username: attributes.email,
                password,
                options: {
                    userAttributes: attributes,
                    autoSignIn: true
                }
            }
        );
    }

    /**
     * Confirm user sign up
     * @param {string} username
     * @param {string} confirmationCode
     * @returns {Promise<void>}
     */
    const userConfirmSignUp = async (username, confirmationCode) => {
        await confirmSignUp({
            username,
            confirmationCode
        });
    }

    /**
     * Resend code for user sign up
     * @param {string} username
     * @returns {Promise<void>}
     */
    const userResendSignUpCode = async (username) => {
        await resendSignUpCode({ username });
    }

    /**
     * Confirm user sign in
     * @param {string} confirmationCode
     * @returns {Promise<void>}
     */
    const userConfirmSignIn = async (confirmationCode) => {
        await confirmSignIn({
            challengeResponse: confirmationCode
        });
    }

    /**
     * Auto sign in user that was just confirmed
     * @returns {Promise<void>}
     */
    const userAutoSignIn = async () => {
        await autoSignIn();
    }

    /**
     * Sign in user without password using OTP code sent via email or WebAuthn (passkey).
     * Returns true if email has been confirmed and false if sign up confirmation is needed.
     * Throws an error if the user does not exist for given email.
     *
     * Possible next steps:
     * - DONE: Sign in successful
     * - CONFIRM_SIGN_UP: User needs to confirm sign up
     * - CONFIRM_SIGN_IN_WITH_EMAIL_CODE: User needs to confirm sign in with email code
     * - CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION: User needs to select first factor (e.g. EMAIL_OTP)
     *
     * @param {string} username
     * @param {string} preferredChallenge - 'EMAIL_OTP' or 'WEB_AUTHN'
     * @returns {Promise<string>} Next step signInStep or null if sign in failed
     */
    const userSignInPasswordless = async (username, preferredChallenge) => {
        const response = await signIn({
            username,
            options: {
                authFlowType: 'USER_AUTH',
                preferredChallenge
            }
        });
        return response?.nextStep?.signInStep ?? null;
    }

    /**
     * Sign out user and clear data
     * @param {boolean} global - If true, sign out globally from Cognito server (across all devices) (default is false)
     * @returns {Promise<void>}
     */
    const userSignOut = async (global = false) => {
        // Sign out from Cognito
        if (isEnvTruthy('USER_ID_SIGN_IN')) {
            await signOut({ global });
        }
        // Clear authentication context data from session storage
        authSignOut();
        // Clear user account context data from browser memory
        clearAccountData();
    }

    /**
     * Delete refresh token from local storage to prevent automatic session refresh.
     * @returns {void}
     */
    const deleteRefreshToken = () => {
        for (const key in localStorage){
            if (key.includes('CognitoIdentityServiceProvider') && key.includes('refreshToken')){
                localStorage.removeItem(key);
            }
        }
    }

    /**
     * Get authentication tokens for current session
     * @returns {Promise<{accessToken: (string|null), idToken: (string|null)}>}
     */
    const getAuthTokens = async () => {
        return await _fetchAuthSession();
    }

    /**
     * Fetch authentication session tokens. If `forceRefresh` is true, it will attempt to refresh the session.
     * @param {boolean} forceRefresh
     * @returns {Promise<{accessToken: (string|null), idToken: (string|null)}
     * @private
     */
    const _fetchAuthSession = async (forceRefresh = false) => {
        try {
            const { tokens } = await fetchAuthSession({ forceRefresh });
            return {
                accessToken: tokens?.accessToken?.toString() ?? null,
                idToken: tokens?.idToken?.toString() ?? null
            };
        } catch (err) {
            console.error(err);
            return {
                accessToken: null,
                idToken: null
            };
        }
    }

    /**
     * Authenticate user with a Cognito access token.
     * Returns uuid to token if provided.
     * @param {string} accessToken
     * @returns {Promise<void>}
     */
    const userAuthenticate = async (accessToken) => {
        const result = await postJson(
            '/online/useraccount/authenticate',
            JSON.stringify({ access_token: accessToken })
        );
        if (result?.token) {
            authInitialize(result.token);
        } else {
            throw new Error('Authentication failed. Token is missing.');
        }
    }

    /**
     * Authenticate user with a UUID token
     * @param {string} uuid
     * @returns {Promise<void>}
     */
    const uuidAuthenticate = async (uuid) => {
        const result = await getJson(`/onlinetoken/${uuid}`);
        if (result?.token) {
            authInitialize(result.token);
        } else {
            throw new Error('Authentication failed. Token is missing.');
        }
    }

    /**
     * Initialize company authentication process.
     * Returns the URL to redirect the user to for company authentication, or null if the URL could
     * not be resolved (e.g. the request failed, such as when the session already expired).
     * @returns {Promise<string|null>}
     */
    const userInitCompanyAuthentication = async () => {
        // Check if the environment variable for company auth URL is set (used in development)
        if (!isEmpty(getEnv('SUOMIFI_AUTH_URL'))) {
            clearAccountData();
            return getEnv('SUOMIFI_AUTH_URL');
        }
        const result = await getJson('/online/useraccount/companyauthurl');
        if (result?.url) {
            clearAccountData(); // Clear account data to force re-fetching after company auth
            return result.url;
        } else {
            if (isDev) {
                console.error(`Company authentication start failed. Url is missing from result: ${JSON.stringify(result)}`);
            }
            return null;
        }
    }

    /**
     * Initialize the user identity verification process.
     * Returns the URL to redirect the user to, or null if the URL could not be resolved
     * (e.g. the request failed, such as when the session already expired).
     * @returns {Promise<string|null>}
     */
    const userInitIdentityVerification = async () => {
        const result = await getJson('/online/useraccount/authurl');
        if (result?.url) {
            let url = result.url;
            // Check if the environment variable for user auth URL is set (used in development)
            if (!isEmpty(getEnv('TUPAS_AUTH_URL'))) {
                url = getEnv('TUPAS_AUTH_URL');
                const queryParams = new URLSearchParams(result.url.split('?')[1]);
                url += `&user_id_uuid=${queryParams.get('user_id_uuid')}`;
            }
            url += `&language=${getLocaleBCP47()}`;
            return url;
        } else {
            if (isDev) {
                console.error(`User identity verification start failed. Url is missing from result: ${JSON.stringify(result)}`);
            }
            return null;
        }
    }

    /**
     * Delete user account along with all associated data.
     * If username is provided, deletes the specific user account link to the ssn.
     * @param {string|undefined} username
     * @returns {Promise<void>}
     */
    const userAccountDelete = async (username = undefined) => {
        let url = '/online/useraccount/userdata';
        if (username !== undefined) {
            url += `/${username}`;
        }
        await deleteJson(url);
    }

    /**
     * Delete company data associated with the user account.
     * @returns {Promise<void>}
     */
    const companyDataDelete = async () => {
        await deleteJson('/online/useraccount/companydata');
    }

    /**
     * Get user accounts linked to ssn
     * @param {boolean} force - If true, forces fetching accounts from server even if already loaded
     * @returns {Promise<*>}
     */
    const userGetAccounts = async (force = false) => {
        if (accounts !== null && !force) {
            return accounts;
        }
        const data = await getJson('/online/useraccount/accounts');
        const newAccounts = data?.userAccounts ?? [];
        updateAccounts(newAccounts);
        return newAccounts;
    }

    /**
     * Check if the user is a person
     * @returns {boolean}
     */
    const userIsPerson = () => {
        return authCustomerType === CUSTOMERTYPE.PERSON;
    }

    /**
     * Check if the user is a company
     * @returns {boolean}
     */
    const userIsCompany = () => {
        return authCustomerType === CUSTOMERTYPE.COMPANY;
    }

    /**
     * Check if the user has an authenticated role
     * @returns {boolean}
     */
    const userIsAuthenticated = () => {
        return authRoles.indexOf(role.authenticated) !== -1;
    }

    /**
     * Check if the user is logged in
     * @returns {boolean}
     */
    const userIsLoggedIn = () => {
        return authLoggedIn;
    }

    /**
     * Get the company name of the user
     * @returns {string}
     */
    const userCompanyName = () => {
        return authCompany;
    }

    /**
     * Get the name of the user
     * @returns {string}
     */
    const userName = () => {
        return authName;
    }

    /**
     * Set the user chosen customer type
     * @param {string|null} customerType
     */
    const userSetChosenCustomerType = (customerType) => {
        authSetChosenCustomerType(customerType);
    }

    /**
     * Check if the user should switch to company type
     * @returns {boolean}
     */
    const userShouldSwitchToCompany = () => {
        return userIsAuthenticated() &&
            authCustomerType === CUSTOMERTYPE.PERSON &&
            authChosenCustomerType === CUSTOMERTYPE.COMPANY;
    }

    /**
     * Resolve url for user identity verification.
     * This is NOT used for MyRopo User ID sign-in (legacy TUPAS only).
     * @param {boolean} company
     * @returns {string}
     */
    const userIdentityVerificationUrl = (company = false) => {
        let authUrl = getEnv('AUTH_URL') ?? '';
        if (!authUrl.includes('?')) {
            authUrl += '?';
        } else if (authUrl.includes('?') && !authUrl.endsWith('?')) {
            authUrl += '&';
        }
        authUrl += `language=${getLocaleBCP47()}`;
        if (company) {
            authUrl += '&customertype=1';
        }
        return authUrl;
    }

    /**
     * Get the personal authentication link.
     * @returns {object|string}
     */
    const personalAuthLink = isEnvTruthy('USER_ID_SIGN_IN')
        ? { pathname: '/user/signin', search: `?customerType=${CUSTOMERTYPE.PERSON}` }
        :  userIdentityVerificationUrl();

    /**
     * Get the company authentication link.
     * @returns {object|string}
     */
    const companyAuthLink = isEnvTruthy('USER_ID_SIGN_IN')
        ? { pathname: '/user/signin', search: `?customerType=${CUSTOMERTYPE.COMPANY}` }
        : (!isEmpty(getEnv('SUOMIFI_AUTH_URL')) ? getEnv('SUOMIFI_AUTH_URL') : userIdentityVerificationUrl(true));

    return {
        uuidAuthenticate,
        userAccounts: accounts,
        userGetAccounts,
        userAccountDelete,
        userSignUp,
        userConfirmSignUp,
        userResendSignUpCode,
        userConfirmSignIn,
        userAutoSignIn,
        userSignOut,
        userSignInPasswordless,
        userIsPerson,
        userIsCompany,
        userIsLoggedIn,
        userIsAuthenticated,
        userAuthenticate,
        userName,
        userCompanyName,
        userNeedsIdentityVerification: authStatus === AUTH_STATUS_TUPAS_NEEDED,
        userChosenTypeIsCompany: authChosenCustomerType === CUSTOMERTYPE.COMPANY,
        userSetChosenCustomerType,
        userShouldSwitchToCompany: userShouldSwitchToCompany(),
        userInitCompanyAuthentication,
        userInitIdentityVerification,
        userHasAccount: authUserHasAccount,
        userIdentityVerificationUrl,
        getAuthTokens,
        companyDataDelete,
        deleteRefreshToken,
        personalAuthLink,
        companyAuthLink
    };
}

export default useUserAccount;
